import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { DEFAULT_KEY_BINDINGS, type CameraSettings, type ColorMode, type InputAction } from '../game/cameraSettings';

type SettingsPanelProps = {
  visible: boolean;
  settings: CameraSettings;
  onSettingsChange: (update: CameraSettings) => void;
  onClose: () => void;
};

const panelStyle: CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(520px, 90vw)',
  maxHeight: '86vh',
  overflowY: 'auto',
  padding: 20,
  background: 'linear-gradient(180deg, rgba(7, 12, 20, 0.97), rgba(4, 8, 14, 0.95))',
  border: '1px solid rgba(155, 185, 212, 0.25)',
  boxShadow: '0 18px 50px rgba(0, 0, 0, 0.5)',
  color: '#edf7ff',
  zIndex: 100,
};

function settingRow(label: string, value: string, accent = '#62ddff'): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  };
}

function stepButton(label: string, ariaLabel: string, onClick: () => void, disabled = false) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        color: '#edf7ff',
        padding: '3px 10px',
        fontSize: 11,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );
}

function volumeRow(
  label: string,
  value: number,
  onDecrease: () => void,
  onIncrease: () => void,
) {
  return (
    <div style={settingRow(label, '')}>
      <span style={{ fontSize: 11, color: 'rgba(231,244,252,0.7)' }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {stepButton('-', `Decrease ${label}`, onDecrease, value <= 0)}
        <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', minWidth: 36, textAlign: 'center' }}>{Math.round(value * 100)}</span>
        {stepButton('+', `Increase ${label}`, onIncrease, value >= 1)}
      </div>
    </div>
  );
}

const COLOR_MODE_OPTIONS: Array<{ value: ColorMode; label: string }> = [
  { value: 'default', label: 'Default' },
  { value: 'deuteranopia', label: 'Deuteranopia' },
  { value: 'protanopia', label: 'Protanopia' },
  { value: 'high-contrast', label: 'High Contrast' },
];

const KEY_BINDING_ROWS: Array<{ action: InputAction; label: string }> = [
  { action: 'forward', label: 'Forward' },
  { action: 'back', label: 'Back' },
  { action: 'left', label: 'Left' },
  { action: 'right', label: 'Right' },
  { action: 'jump', label: 'Jump / Jet' },
  { action: 'fire', label: 'Fire' },
  { action: 'grenade', label: 'Grenade' },
  { action: 'pack', label: 'Pack' },
  { action: 'mine', label: 'Mine / Ping' },
  { action: 'scoreboard', label: 'Scoreboard' },
  { action: 'teamSwitch', label: 'Team Switch' },
];

function toggleButton(label: string, pressed: boolean, onClick: () => void) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      onClick={onClick}
      style={{
        background: pressed ? '#62ddff20' : 'rgba(255,255,255,0.06)',
        border: `1px solid ${pressed ? '#62ddff60' : 'rgba(255,255,255,0.12)'}`,
        color: pressed ? '#62ddff' : '#edf7ff',
        padding: '3px 12px',
        fontSize: 10,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {pressed ? 'ON' : 'OFF'}
    </button>
  );
}

export function SettingsPanel({ visible, settings, onSettingsChange, onClose }: SettingsPanelProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [rebindingAction, setRebindingAction] = useState<InputAction | null>(null);

  useEffect(() => {
    if (!visible) return;
    panelRef.current?.focus();
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tribes2-settings-title"
      tabIndex={-1}
      onKeyDown={(event) => {
        if (rebindingAction) {
          event.preventDefault();
          onSettingsChange({
            ...settings,
            keyBindings: {
              ...settings.keyBindings,
              [rebindingAction]: event.code,
            },
          });
          setRebindingAction(null);
          return;
        }
        if (event.key === 'Escape') {
          onClose();
        }
      }}
      style={panelStyle}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span id="tribes2-settings-title" style={{ fontSize: 11, letterSpacing: 2.2, textTransform: 'uppercase', color: '#62ddff', fontWeight: 700 }}>
          Settings
        </span>
        <button
          type="button"
          aria-label="Close settings"
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: '#edf7ff',
            padding: '3px 10px',
            fontSize: 10,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          ESC
        </button>
      </div>

      <div style={settingRow('Sensitivity', '')}>
        <span style={{ fontSize: 11, color: 'rgba(231,244,252,0.7)' }}>Sensitivity</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {stepButton('-', 'Decrease sensitivity', () => onSettingsChange({ ...settings, sensitivity: Math.max(0.25, settings.sensitivity - 0.1) }), settings.sensitivity <= 0.25)}
          <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', minWidth: 36, textAlign: 'center' }}>{settings.sensitivity.toFixed(1)}</span>
          {stepButton('+', 'Increase sensitivity', () => onSettingsChange({ ...settings, sensitivity: Math.min(2.5, settings.sensitivity + 0.1) }), settings.sensitivity >= 2.5)}
        </div>
      </div>

      <div style={settingRow('FOV', '')}>
        <span style={{ fontSize: 11, color: 'rgba(231,244,252,0.7)' }}>Field of View</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {stepButton('-', 'Decrease field of view', () => onSettingsChange({ ...settings, fov: Math.max(68, settings.fov - 2) }), settings.fov <= 68)}
          <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', minWidth: 36, textAlign: 'center' }}>{Math.round(settings.fov)}</span>
          {stepButton('+', 'Increase field of view', () => onSettingsChange({ ...settings, fov: Math.min(108, settings.fov + 2) }), settings.fov >= 108)}
        </div>
      </div>

      <div style={settingRow('Invert Y', '')}>
        <span style={{ fontSize: 11, color: 'rgba(231,244,252,0.7)' }}>Invert Y</span>
        <button
          type="button"
          aria-label="Toggle invert Y"
          aria-pressed={settings.invertY}
          onClick={() => onSettingsChange({ ...settings, invertY: !settings.invertY })}
          style={{
            background: settings.invertY ? '#62ddff20' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${settings.invertY ? '#62ddff60' : 'rgba(255,255,255,0.12)'}`,
            color: settings.invertY ? '#62ddff' : '#edf7ff',
            padding: '3px 12px',
            fontSize: 10,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {settings.invertY ? 'ON' : 'OFF'}
        </button>
      </div>

      <div style={settingRow('HUD Scale', '')}>
        <span style={{ fontSize: 11, color: 'rgba(231,244,252,0.7)' }}>HUD Scale</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {stepButton('-', 'Decrease HUD scale', () => onSettingsChange({ ...settings, hudScale: Math.max(0.7, +(settings.hudScale - 0.1).toFixed(1)) }), settings.hudScale <= 0.7)}
          <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', minWidth: 36, textAlign: 'center' }}>{settings.hudScale.toFixed(1)}</span>
          {stepButton('+', 'Increase HUD scale', () => onSettingsChange({ ...settings, hudScale: Math.min(1.4, +(settings.hudScale + 0.1).toFixed(1)) }), settings.hudScale >= 1.4)}
        </div>
      </div>

      <div style={{ ...settingRow('Reduced Motion', ''), borderBottom: 'none' }}>
        <span style={{ fontSize: 11, color: 'rgba(231,244,252,0.7)' }}>Reduced Motion</span>
        <button
          type="button"
          aria-label="Toggle reduced motion"
          aria-pressed={settings.reducedMotion}
          onClick={() => onSettingsChange({ ...settings, reducedMotion: !settings.reducedMotion })}
          style={{
            background: settings.reducedMotion ? '#62ddff20' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${settings.reducedMotion ? '#62ddff60' : 'rgba(255,255,255,0.12)'}`,
            color: settings.reducedMotion ? '#62ddff' : '#edf7ff',
            padding: '3px 12px',
            fontSize: 10,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {settings.reducedMotion ? 'ON' : 'OFF'}
        </button>
      </div>

      <div style={settingRow('Audio Mute', '')}>
        <span style={{ fontSize: 11, color: 'rgba(231,244,252,0.7)' }}>Audio Mute</span>
        <button
          type="button"
          aria-label="Toggle audio mute"
          aria-pressed={settings.audioMuted}
          onClick={() => onSettingsChange({ ...settings, audioMuted: !settings.audioMuted })}
          style={{
            background: settings.audioMuted ? '#ff8d7420' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${settings.audioMuted ? '#ff8d7460' : 'rgba(255,255,255,0.12)'}`,
            color: settings.audioMuted ? '#ffb39f' : '#edf7ff',
            padding: '3px 12px',
            fontSize: 10,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {settings.audioMuted ? 'ON' : 'OFF'}
        </button>
      </div>

      {volumeRow(
        'Master Volume',
        settings.masterVolume,
        () => onSettingsChange({ ...settings, masterVolume: Math.max(0, +(settings.masterVolume - 0.1).toFixed(1)) }),
        () => onSettingsChange({ ...settings, masterVolume: Math.min(1, +(settings.masterVolume + 0.1).toFixed(1)) }),
      )}
      {volumeRow(
        'Combat Volume',
        settings.combatVolume,
        () => onSettingsChange({ ...settings, combatVolume: Math.max(0, +(settings.combatVolume - 0.1).toFixed(1)) }),
        () => onSettingsChange({ ...settings, combatVolume: Math.min(1, +(settings.combatVolume + 0.1).toFixed(1)) }),
      )}
      {volumeRow(
        'Movement Volume',
        settings.movementVolume,
        () => onSettingsChange({ ...settings, movementVolume: Math.max(0, +(settings.movementVolume - 0.1).toFixed(1)) }),
        () => onSettingsChange({ ...settings, movementVolume: Math.min(1, +(settings.movementVolume + 0.1).toFixed(1)) }),
      )}
      {volumeRow(
        'Announcer Volume',
        settings.announcerVolume,
        () => onSettingsChange({ ...settings, announcerVolume: Math.max(0, +(settings.announcerVolume - 0.1).toFixed(1)) }),
        () => onSettingsChange({ ...settings, announcerVolume: Math.min(1, +(settings.announcerVolume + 0.1).toFixed(1)) }),
      )}

      <div style={{ marginTop: 14, marginBottom: 6, fontSize: 11, letterSpacing: 1.8, textTransform: 'uppercase', color: '#62ddff', fontWeight: 700 }}>
        Accessibility
      </div>

      <div style={settingRow('Color Mode', '')}>
        <span style={{ fontSize: 11, color: 'rgba(231,244,252,0.7)' }}>Color Mode</span>
        <select
          aria-label="Select color mode"
          value={settings.colorMode}
          onChange={(event) => onSettingsChange({ ...settings, colorMode: event.currentTarget.value as ColorMode })}
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: '#edf7ff',
            padding: '4px 8px',
            fontSize: 11,
            fontFamily: 'inherit',
          }}
        >
          {COLOR_MODE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      <div style={settingRow('Captions', '')}>
        <span style={{ fontSize: 11, color: 'rgba(231,244,252,0.7)' }}>Captions</span>
        {toggleButton('Toggle captions', settings.captionsEnabled, () => onSettingsChange({ ...settings, captionsEnabled: !settings.captionsEnabled }))}
      </div>

      <div style={settingRow('Touch Controls', '')}>
        <span style={{ fontSize: 11, color: 'rgba(231,244,252,0.7)' }}>Touch Controls</span>
        {toggleButton('Toggle touch controls', settings.touchControlsEnabled, () => onSettingsChange({ ...settings, touchControlsEnabled: !settings.touchControlsEnabled }))}
      </div>

      {/* Phase 170 — graphics & gameplay tuning controls. */}
      <div style={{ marginTop: 14, marginBottom: 6, fontSize: 11, letterSpacing: 1.8, textTransform: 'uppercase', color: '#62ddff', fontWeight: 700 }}>
        Graphics
      </div>
      <div style={settingRow('Shadows', '')}>
        <span style={{ fontSize: 11, color: 'rgba(231,244,252,0.7)' }}>Shadows</span>
        {toggleButton('Toggle shadows', settings.shadowsEnabled, () => onSettingsChange({ ...settings, shadowsEnabled: !settings.shadowsEnabled }))}
      </div>
      {volumeRow(
        'Bloom Intensity',
        settings.bloomIntensity,
        () => onSettingsChange({ ...settings, bloomIntensity: Math.max(0, +(settings.bloomIntensity - 0.1).toFixed(1)) }),
        () => onSettingsChange({ ...settings, bloomIntensity: Math.min(1, +(settings.bloomIntensity + 0.1).toFixed(1)) }),
      )}
      {volumeRow(
        'Particle Density',
        settings.particleDensity,
        () => onSettingsChange({ ...settings, particleDensity: Math.max(0, +(settings.particleDensity - 0.1).toFixed(1)) }),
        () => onSettingsChange({ ...settings, particleDensity: Math.min(1, +(settings.particleDensity + 0.1).toFixed(1)) }),
      )}

      <div style={{ marginTop: 12, marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontSize: 11, letterSpacing: 1.8, textTransform: 'uppercase', color: '#62ddff', fontWeight: 700 }}>Key Bindings</span>
        <button
          type="button"
          aria-label="Reset key bindings"
          onClick={() => onSettingsChange({ ...settings, keyBindings: DEFAULT_KEY_BINDINGS })}
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: '#edf7ff',
            padding: '3px 10px',
            fontSize: 10,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          RESET
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 6 }}>
        {KEY_BINDING_ROWS.map((row) => (
          <button
            key={row.action}
            type="button"
            aria-label={`Rebind ${row.action}`}
            onClick={() => setRebindingAction(row.action)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              background: rebindingAction === row.action ? '#62ddff20' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${rebindingAction === row.action ? '#62ddff70' : 'rgba(255,255,255,0.09)'}`,
              color: '#edf7ff',
              padding: '6px 8px',
              fontSize: 10,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <span style={{ color: 'rgba(231,244,252,0.72)' }}>{row.label}</span>
            <span style={{ color: '#62ddff', fontVariantNumeric: 'tabular-nums' }}>
              {rebindingAction === row.action ? 'PRESS KEY' : settings.keyBindings[row.action]}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
